import { useNavigate} from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Spinner from "../../Utils/spinner/loading";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUploadVesselDocuments } from "../utils/useUploadVesselDocuments";
import useUploadVesselDocumentsWithAuth from "../utils/useUploadVesselDocumentsWithAuth"; 
import { FaRegFile } from "react-icons/fa";
import { HiX, HiArrowLeft, HiOutlineCloud } from "react-icons/hi";
import { ReactComponent as VatugIcon } from "../../Asset/black.svg";
import { MdDone } from "react-icons/md";
import "../create_vessels/createvessel.css";

const VesselPrimaryDoc = () => {
  const navigate = useNavigate();
  const { mutate, isLoading, error: mutationError } = useUploadVesselDocuments();
  const { skipMutation, isLoading: isLoadingSkip, error: errorSkip, isSuccess: successSkip } = useUploadVesselDocumentsWithAuth();
  const [specSheet, setSpecSheet] = useState(null);
  const [bollardPullCertificate, setBollardPullCertificate] = useState(null);
  const [navyCertificate, setNavyCertificate] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const vesselType = localStorage.getItem("selectedVesselType");
  const specSheetRef = useRef(null);
  const bollardPullCertificateRef = useRef(null);
  const navyCertificateRef = useRef(null);

  useEffect(() => {
    if (mutationError) {
      toast.error(mutationError.message || "An error occurred during document upload. Please try again.");
    }
    if (errorSkip) {
      toast.error(errorSkip.message || "An error occurred while skipping. Please try again.");
    }
    if (successSkip) {
      toast.success("Skipped additional document uploads!");
      navigate("/vesselsummary");
    }
  }, [mutationError, errorSkip, successSkip, navigate]);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files[0]) return;
    
    if (files[0].type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    if (name === "specSheet") {
      setSpecSheet(files[0]);
    } else if (name === "bollardPullCertificate") {
      setBollardPullCertificate(files[0]);
    } else if (name === "navyCertificate") {
      setNavyCertificate(files[0]);
    }
  };

  const handleRemoveFile = (name) => {
    if (name === "specSheet") {
      setSpecSheet(null);
      if (specSheetRef.current) specSheetRef.current.value = "";
    } else if (name === "bollardPullCertificate") {
      setBollardPullCertificate(null);
      if (bollardPullCertificateRef.current) bollardPullCertificateRef.current.value = "";
    } else if (name === "navyCertificate") {
      setNavyCertificate(null);
      if (navyCertificateRef.current) navyCertificateRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!specSheet) {
      toast.error("Please upload the Spec Sheet.");
      return;
    }

    const formData = new FormData();
    formData.append("specSheet", specSheet);
    if (vesselType === "AHTS 001" && bollardPullCertificate) {
      formData.append("bollardPullCertificate", bollardPullCertificate);
    }
    if (vesselType === "SEV 001" && navyCertificate) {
      formData.append("navyCertificate", navyCertificate);
    }

    mutate(formData, {
      onError: (err) => {
        toast.error(err.message || "An error occurred during document upload. Please try again.");
      },
      onSuccess: () => {
        toast.success("Documents uploaded successfully!");
        setShowOverlay(true);  
      },
    });
  };

  const formatFileSize = (size) => {
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(2)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleOverlayYes = () => {
    navigate("/vesselotherdocs");
  };

  const handleOverlayNo = () => {
    skipMutation.mutate(); 
  };

  const renderUploadArea = (title, file, inputRef, inputName) => (
    <div className="upload-section">
      <p className="upload-title">{title}</p>
      {!file ? (
        <div className="upload-area" onClick={() => inputRef.current.click()}>
          <div className="upload-placeholder">
            <HiOutlineCloud className="upload-icon" />
            <p>Drop pdf here,</p>
            <p className="browse-text">or browse</p>
          </div>
          <p className="supported-formats">Supports: PDF</p>
        </div>
      ) : (
        <div className="file-preview">
          <div className="file-info">
            <FaRegFile className="file-icon" />
            <span className="file-name">{file.name}</span>
            <span className="file-size">({formatFileSize(file.size)})</span>
          </div>
          <div className="file-actions">
            <MdDone className="success-icon" />
            <button 
              className="change-file"
              onClick={() => inputRef.current.click()}
            >
              Change
            </button>
            <button 
              className="remove-preview"
              onClick={() => handleRemoveFile(inputName)}
            >
              <HiX />
            </button>
          </div>
        </div>
      )}
      <input
        type="file"
        name={inputName}
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".pdf"
      />
    </div>
  );

  const Overlay = () => (
    <div className="modal-overlays">
      <div className="modal-content">
        <h2>Additional Documents</h2>
        <p>Would you like to upload additional documents for your vessel?</p>
        <div className="modal-buttons">
          <button onClick={handleOverlayYes} disabled={isLoadingSkip}>
            Yes
          </button>
          <button onClick={handleOverlayNo} disabled={isLoadingSkip} className="active">
            No
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="create-vessel-container">
      <div className="create-vessel-header-bar">
        <div className="logo-container">
          <VatugIcon />
          <div className="logo-underline"></div>
        </div>
        <div className="title">Vessel Upload</div>
        <button className="close-btn" onClick={() => navigate("/dashboard/myvessels")}><HiX /></button>
      </div>

      <div className="create-vessel-card">
        <h2>Certificate</h2>
        
        {renderUploadArea("Spec Sheet", specSheet, specSheetRef, "specSheet")}
        
        {vesselType === "AHTS 001" && (
          renderUploadArea("Bollard Pull Certificate", bollardPullCertificate, bollardPullCertificateRef, "bollardPullCertificate")
        )}

        {vesselType === "SEV 001" && (
          renderUploadArea("Navy Certificate", navyCertificate, navyCertificateRef, "navyCertificate")
        )}

        <div className="button-row">
          <button className="back-btn" onClick={() => navigate("/vesselimages")}>
            <HiArrowLeft style={{marginRight: 6}}/> Go Back
          </button>
          <button 
            className="next-btn" 
            onClick={handleSubmit}
            disabled={isLoading || !specSheet}
          >
            {isLoading ? <Spinner /> : "Next"}
          </button>
        </div>
      </div>

      {showOverlay && <Overlay />}
      <ToastContainer />
    </div>
  );
};

export default VesselPrimaryDoc;
